/**
 * Kleurenrampen voor de radiale poort-visualisatie. Alle drie starten op het
 * bestaande dusk-blauw en lopen laag per laag door het spectrum, met stijgende
 * lichtheid naar buiten toe.
 *
 * Deze kleuren coderen uitsluitend DIEPTE. Ze staan bewust los van de
 * kleursemantiek van de bodygraph (centerkleuren, defined/undefined,
 * personality/design) — zie de spec §4.2. De enige plek waar die bestaande
 * semantiek wél geldt is de chart-highlight, die zwart en rood gebruikt.
 */
export type HdGateGraphPaletteId = 'aarde' | 'mandala' | 'prisma' | 'whales-of-personality';

export interface HdGateGraphPalette {
  readonly id: HdGateGraphPaletteId;
  readonly labelKey: string;
  /** Index = laag 0 t/m 4. */
  readonly colors: readonly [string, string, string, string, string];
  /** Eigen vulling + tekst van de poortcirkel; zonder valt ze terug op het dusk-blauw van de app. */
  readonly gateFill?: string;
  readonly gateText?: string;
}

export const HD_GATE_GRAPH_PALETTES: readonly HdGateGraphPalette[] = [
  {
    id: 'aarde',
    labelKey: 'humanDesign.gateGraph.palette.aarde',
    // schemering → water → mos → zand; eindigt op het bestaande amber-accent.
    colors: ['#2c4a6e', '#3a7d97', '#4f9b8c', '#93a97e', '#d4a869'],
  },
  {
    id: 'mandala',
    labelKey: 'humanDesign.gateGraph.palette.mandala',
    // Gelijkmatige hue-stappen van +35°, zonder groentinten.
    colors: ['#2c4a6e', '#4a5b96', '#6b5f9e', '#94679a', '#c07f92'],
  },
  {
    id: 'prisma',
    labelKey: 'humanDesign.gateGraph.palette.prisma',
    // Veel wijdere boog (±210° hue), maar elke stap licht gebroken en warm
    // gehouden zodat het één familie blijft in plaats van een regenboog.
    colors: ['#2c4a6e', '#2f7d80', '#6f9459', '#cf9a4a', '#c2605f'],
  },
  {
    id: 'whales-of-personality',
    labelKey: 'humanDesign.gateGraph.palette.whales-of-personality',
    // Whale: navy (bewustzijn) → dusk-blauw → whale-rood (motor) →
    // terracotta → amber (expressie). Blauw binnen, warm naar buiten; het
    // lijncijfer (laag 1) staat in dusk-blauw goed leesbaar op de lichte
    // lijncirkel en de kleurbolletjes (laag 2) dragen wit op het rood.
    colors: ['#043551', '#285a82', '#7b241c', '#b0603f', '#d2ad76'],
    // De centrale poortcirkel in het whale-navy, met gebroken wit.
    gateFill: '#043551',
    gateText: '#f7f4ee',
  },
];

export const HD_GATE_GRAPH_PALETTE_DEFAULT: HdGateGraphPaletteId = 'aarde';

/** Geeft altijd een ramp terug; een onbekende id valt terug op de default. */
export function resolveHdGateGraphPalette(id: string | undefined): HdGateGraphPalette {
  return HD_GATE_GRAPH_PALETTES.find(palette => palette.id === id) ?? HD_GATE_GRAPH_PALETTES[0];
}

/** Mengt een kleur met wit. `share` = aandeel kleur: 0 is wit, 1 is de kleur zelf. */
export function hdGateGraphTint(hex: string, share: number): string {
  const value = parseInt(hex.slice(1), 16);
  const mix = (channel: number): number => Math.round(channel * share + 255 * (1 - share));
  const channels = [(value >> 16) & 255, (value >> 8) & 255, value & 255];
  return `#${channels.map(mix).map(channel => channel.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Vulling van de lijncirkels: een subtiel warm amber. Bewust palet-onafhankelijk
 * zodat de afwisseling vol → licht → vol (poort, lijn, kleur) in elke ramp
 * hetzelfde ritme houdt.
 */
const LINE_FILL = '#f7edda';

/**
 * Vulling van de poortcirkel: het donkere dusk-blauw van de app-knoppen.
 * Palet-onafhankelijk, net als {@link LINE_FILL} — de rampen kleuren de
 * hiërarchie, deze twee dragen het vol-licht-vol ritme. Een thema-palet
 * (Whale) mag de poortcirkel overschrijven via `gateFill`.
 */
const GATE_FILL = 'rgb(71, 82, 96)';

/** Tekstkleur op de gevulde poortcirkel. */
const GATE_TEXT = '#ffffff';

/**
 * Custom properties voor de host. Een ramp-wissel is daarmee het herzetten van
 * enkele properties in plaats van het opnieuw opbouwen van de hele tekening.
 *
 * De poortcirkel is omgekeerd ten opzichte van de rest: hij is VOL in het
 * donkere dusk-blauw met witte tekst, de lijncirkel is licht amber, en de
 * kleurbolletjes zijn weer vol. Dat vol-licht-vol-ritme geeft het contrast.
 */
export function buildHdGateGraphPaletteVars(palette: HdGateGraphPalette): Record<string, string> {
  const vars: Record<string, string> = {};
  palette.colors.forEach((color, layer) => {
    vars[`--hd-gate-graph-layer-${layer}`] = color;
  });
  vars['--hd-gate-graph-fill-0'] = palette.gateFill ?? GATE_FILL;
  vars['--hd-gate-graph-fill-1'] = LINE_FILL;
  vars['--hd-gate-graph-on-0'] = palette.gateText ?? GATE_TEXT;
  return vars;
}
